/**
 * One umpire's card on a Match Debrief screen — the parts BOTH screens draw.
 *
 * Authority: `um-app/13-match-debrief.md` §3 and §5; `um-app/12-ai-features.md` §4a (Call 6);
 * the Technical Delegate's rulings of **11 September 2026** (U1, U2, U3) recorded in spec 13 §3;
 * spec 13 §9 (run 75 — the Technical Delegate's read-only view).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS MODULE EXISTS, AND WHAT IS DELIBERATELY NOT IN IT
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Two screens now draw the same umpire card from the same bytes: `pages/um/UmMatchDebriefPage`,
 * where a manager holds the debrief and writes down what the umpire said, and
 * `pages/TdMatchDebriefPage`, where the Technical Delegate reads what the panel released. The
 * seat header, the organised summary and the released-observations list are identical on both —
 * same classes, same `data-testid`s, same strings — so they are written once here.
 *
 * **Every write control is absent from this module, and that is a property rather than an
 * omission.** The composer, `Release Responses`, `Lock`, `Undo Release` and the unlock
 * confirmation live whole inside `UmMatchDebriefPage`. A shared component taking an `onRelease`
 * prop that one caller left undefined would put a release path one careless line away from the
 * Technical Delegate's screen; there is no such prop and no such path here.
 *
 * **Room-safety is carried on one flag, and the alignment sentence on a second.** `roomView` is
 * ruling U1's switch: it hides the author chips, the note counts and the `worth aligning` closing
 * sentence. The UM screen toggles it; the TD screen holds it permanently true, for the reason
 * `TdMatchDebriefPage` gives at length. **Run 80** gave the alignment sentence its own switch,
 * `showAlignment`, because the Technical Delegate decided on 13 September 2026 that his screen
 * shows that sentence while keeping the rest of the room-safe set. It defaults to `!roomView`, so
 * the phone behaves exactly as it did. Nothing here reads a preference, a storage key or a search
 * parameter.
 */

import type { AppointedUmpireBody, MatchDebriefSummaryBody, ReleasedNoteView } from "../../api/types.js";
import {
  criterionName,
  noteStamp,
  SEAT_LABEL,
  seatInitials,
  splitSummary,
  summaryStamp,
  tokyoClockTime,
} from "../../pages/um/matchDebrief.js";

/**
 * One entry of the per-umpire summary read: the body, or the failure of that one read.
 *
 * A rejected read is `{ ok: false }` rather than a rejection of the whole set — the debrief still
 * has two other umpires in it, and a screen that went blank because one summary could not be
 * built would have thrown away two summaries and every released note on it.
 */
export type SummaryEntry =
  | { tmsPersonId: string; ok: true; body: MatchDebriefSummaryBody }
  | { tmsPersonId: string; ok: false };

/**
 * The umpire header — avatar, media name, country, slot code and the slot in plain words.
 *
 * **The role code is on every card, in both views and on both screens**: which seat a person was
 * in is the first thing a reader needs and is nobody's private fact.
 */
export function DebriefSeatHeader({ seat }: { seat: AppointedUmpireBody }): React.JSX.Element {
  return (
    <div className="mduhead">
      <span className="dbrow__avatar" aria-hidden="true">
        {seatInitials(seat.name)}
      </span>
      <span className="dbrow__nm">
        {seat.name}
        <span className="dbrow__sub num">
          {`${seat.country} · ${seat.slot} · ${SEAT_LABEL[seat.slot as "U1" | "U2" | "VU"]}`}
        </span>
      </span>
    </div>
  );
}

/**
 * THE ORGANISED SUMMARY — Call 6's text, in the house summary treatment.
 *
 * It is drawn in `.dbainote`'s wash and `.dbrow__ai`'s text and **not in dark red**: `--alert` is
 * this application's error semantic, and a summary in the error colour would tell a reader at a
 * glance that something had gone wrong with a text that is simply the panel's own words
 * rearranged.
 *
 * The four states are the API's and this component invents none of them. `null` entry is the read
 * still in flight; `ok: false` is that one umpire's failed read, said plainly with the notes
 * still below it; `no_notes` is the honest empty answer; anything else renders the text.
 *
 * **The `no_notes` notice has two forms, and Room View gets the shorter one.** The API's own
 * `notice` names a manager's unreleased notes, which is the panel's own business and not a
 * sentence to leave on a table — nor one to re-present on a Control Centre screen.
 */
export function DebriefSeatSummary({
  seat,
  entry,
  roomView,
  showAlignment,
}: {
  seat: AppointedUmpireBody;
  entry: SummaryEntry | null;
  roomView: boolean;
  /**
   * Whether the `For The Managers` alignment sentence is drawn. Omitted, it follows ruling U1 —
   * outside Room View only — which is what the phone gets by passing nothing. The Technical
   * Delegate's read-only screen passes `true` (run 80, his decision of 13 September 2026).
   */
  showAlignment?: boolean;
}): React.JSX.Element {
  const body = entry !== null && entry.ok ? entry.body : null;
  const split =
    body?.summary === null || body?.summary === undefined
      ? null
      : splitSummary(body.summary, body.alignments.length > 0);
  const stamp =
    body === null ? null : summaryStamp(tokyoClockTime(body.generatedAt ?? ""), body.noteCount);
  const alignmentShown = showAlignment ?? !roomView;

  if (entry === null) return <p className="mdai__notice">Reading The Summary…</p>;
  if (!entry.ok) {
    return (
      <p className="mdai__notice" data-testid={`md-summary-failed-${seat.tmsPersonId}`}>
        The summary could not be read just now. The released notes for this match are below in
        full, and they are the evidence either way.
      </p>
    );
  }
  if (body !== null && body.state === "no_notes") {
    return (
      <p className="mdai__notice" data-testid={`md-none-${seat.tmsPersonId}`}>
        {roomView ? "No Released Notes Yet For This Match" : body.notice}
      </p>
    );
  }
  if (split === null) return <p className="mdai__notice">{body?.notice ?? ""}</p>;

  return (
    <div className="mdai" data-testid={`md-summary-${seat.tmsPersonId}`}>
      {/* Run 73d: the block announces itself. Live testing found the TD reading straight past
          the summary because nothing named it — quiet is right, unlabelled was too quiet. */}
      <span className="card__kicker" data-testid={`md-ai-kicker-${seat.tmsPersonId}`}>
        AI Summary
      </span>
      {split.body.map((paragraph, index) => (
        <p className="mdai__p" key={index}>
          {paragraph}
        </p>
      ))}
      {/* RULING U1 — on the phone the alignment renders outside Room View alone, under its own
          kicker. Run 80's `showAlignment` also draws it on the Technical Delegate's screen. */}
      {alignmentShown && split.alignment !== null ? (
        <>
          <span className="card__kicker" data-testid={`md-align-kicker-${seat.tmsPersonId}`}>
            For The Managers
          </span>
          <p className="mdai__p" data-testid={`md-align-${seat.tmsPersonId}`}>
            {split.alignment}
          </p>
        </>
      ) : null}
      {stamp === null ? null : (
        <span className="mdai__stamp num" data-testid={`md-stamp-${seat.tmsPersonId}`}>
          {stamp}
        </span>
      )}
    </div>
  );
}

/**
 * The released OBSERVATIONS about one umpire — the evidence the summary was built from.
 *
 * The author chip of spec 04 §2.4 (`UM · Naoko`) is drawn outside Room View only. Attribution is
 * the panel's business: an umpire reading `UM · Naoko` beside a sentence across the table has
 * learnt which manager wrote which note, which is a fact about the panel that neither screen was
 * built to disclose.
 */
export function DebriefSeatObservations({
  observations,
  roomView,
}: {
  observations: readonly ReleasedNoteView[];
  roomView: boolean;
}): React.JSX.Element {
  if (observations.length === 0) {
    return <p className="card__body">Nothing has been released about this umpire yet.</p>;
  }
  return (
    <ul className="mdnotes">
      {observations.map((note) => (
        <li className="mdnote" key={note.id}>
          <span className="mdnote__crit">{criterionName(note.criterionStableKey)}</span>
          <span className="mdnote__when num">{noteStamp(note)}</span>
          {roomView ? null : (
            <span className="mdnote__au num">{`UM · ${note.authorShortName ?? "Unattributed"}`}</span>
          )}
          <span className="mdnote__txt">{note.bodyEn}</span>
        </li>
      ))}
    </ul>
  );
}
